import { StatusIdeaCard } from 'components/Card'
import { GRID_BREAKPOINTS } from 'components/ContentGridView'
import { FetchCuratedFolderPageContent } from 'components/Fetch'
import ScrollListener from 'components/ScrollListener'
import { CuratedFolder, INTERACTION_EVENT_OPEN, InteractionEvent, StatusIdea } from 'interfaces'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { renderLoadingCards } from 'routes/find/routes/home/components/utils'
import styles from './StatusesBricksView.pcss'

export interface ScrollConfiguration {
  element?: Element
  useWindow?: boolean
}

export interface StatusesBricksViewProps {
  folder: CuratedFolder
  items: StatusIdea[]
  seed: string
  startPage?: number
  scroll?: ScrollConfiguration
  trackInteraction?(categoryIds: string[], event: InteractionEvent): Promise<any>
  onItemShared?(folder: CuratedFolder, content: StatusIdea): void
}

export function StatusesBricksView(props: StatusesBricksViewProps) {
  const { folder, items: initialItems, seed, startPage, scroll, trackInteraction } = props

  const [page, setPage] = useState(startPage || 0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<StatusIdea[]>(initialItems)

  useEffect(() => {
    setLoading(true)
    setHasNextPage(true)
    setPage(startPage || 0)
    setItems(initialItems)
  }, [seed, initialItems, startPage])

  useEffect(() => {
    if (trackInteraction) {
      trackInteraction(folder.streams.map(s => s.id.toString()), INTERACTION_EVENT_OPEN)
    }
  }, [trackInteraction, folder.streams])

  const scrollElement = useMemo(() => {
    return scroll?.element || document.getElementsByTagName('main')[0]
  }, [scroll])

  const loadNextPage = useCallback(() => {
    if (!hasNextPage || loading) {
      return
    }
    setPage((prevPage) => {
      setLoading(true)
      return prevPage + 1
    })
  }, [hasNextPage, loading])

  const onFetched = useCallback((data: StatusIdea[]) => {
    setLoading(false)
    setHasNextPage(data.length > 0)
    setItems((prevItems) => [...prevItems, ...data])
  }, [])

  const onFetchError = useCallback(() => {
    setLoading(false)
  }, [])

  return (
    <Fragment>
      <FetchCuratedFolderPageContent
        page={page}
        folder={folder}
        statusesSeed={seed}
        onFetched={onFetched}
        onFailed={onFetchError}
      />
      <ScrollListener
        className={styles.bricks}
        disabled={loading || !hasNextPage}
        emitTreshold={250}
        onScrollDebounceTime={50}
        emitInitial={false}
        useWindow={scroll?.useWindow}
        scrollElement={scrollElement}
        onScroll={loadNextPage}
      >
        <ResponsiveMasonry columnsCountBreakPoints={GRID_BREAKPOINTS}>
          <Masonry gutter="12px" className={styles.masonry}>
            {items.map((item) => (
              <StatusIdeaCard key={item.id} content={item} />
            ))}
            {loading && renderLoadingCards()}
          </Masonry>
        </ResponsiveMasonry>
      </ScrollListener>
    </Fragment>
  )
}

export default StatusesBricksView
